import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Trash2, Loader2, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../lib/admin';

// Edge functions verify the caller's own session, so send the user's access
// token — never the anon key.
async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
}

interface User {
  id: string;
  email: string;
  created_at: string;
}

export function AdminSection() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (admin) {
      fetchUsers();
    }
  }, [admin]);

  async function fetchUsers() {
    try {
      // Call the edge function to list users
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-users`, {
        headers: await authHeaders(),
      });

      if (!response.ok) throw new Error('Failed to fetch users');
      
      const { users } = await response.json();
      setUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Could not load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteUser(userId: string) {
    if (!confirm('Are you sure you want to delete this user? This cannot be undone.')) {
      return;
    }

    try {
      setDeleting(userId);
      
      // Call the edge function to delete user
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(await authHeaders()),
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) throw new Error('Failed to delete user');

      setUsers(users.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Could not delete user. Please try again.');
    } finally {
      setDeleting(null);
    }
  }

  if (!admin) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <div className="flex items-center">
          <Users className="h-5 w-5 text-gray-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">User Management</h3>
        </div>
      </div>
      <ul className="divide-y divide-gray-200">
        {users.map(user => (
          <li key={user.id} className="px-4 py-4 sm:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-indigo-600">{user.email}</p>
                <p className="text-sm text-gray-500">
                  Created: {new Date(user.created_at).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => handleDeleteUser(user.id)}
                disabled={deleting === user.id}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting === user.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}